/**
 * @file InvoiceCorrectionPanel.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-01
 * @modified 2026-11-01
 * @description "Needs correcting" on an invoice (Cam, 30 Oct 2026 — units
 *              corrected on a delivered order after its invoice was paid: the
 *              invoice is NOT changed, it is flagged for a person to put
 *              right; services/invoiceCorrectionFlagService).
 *
 *              InvoiceCorrectionBadge — the pill on Search Invoices' row (and
 *              anywhere else an invoice shows), its facts in the title.
 *              InvoiceCorrectionPanel — the invoice window's block: each open
 *              flag's words (what changed, the money difference, Xero's lock)
 *              and "Dealt with…" (RepFlagResolveModal, with the customer's
 *              documents — the credit or debit note that put it right — and
 *              a note; both optional). It clears only when marked dealt with.
 */

import React, { useState } from 'react';
import RepFlagResolveModal from './RepFlagResolveModal';
import { API_URL } from '../config';
import './InvoiceCorrectionPanel.css';

const money = (n) => `£${Math.abs(Number(n) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** The badge's words in its title: each flag's note. */
export function correctionTitle(flags) {
  return (flags || []).map(f => f.note).filter(Boolean).join('\n\n') || 'Needs correcting';
}

export function InvoiceCorrectionBadge({ flags }) {
  if (!Array.isArray(flags) || !flags.length) return null;
  return (
    <span className="invoice-correction-badge" title={correctionTitle(flags)}>
      <span className="material-symbols-rounded" aria-hidden="true">edit_document</span>
      Needs correcting
    </span>
  );
}

function InvoiceCorrectionPanel({ flags, customerName, canResolve = true, onResolved }) {
  const [resolving, setResolving] = useState(null);
  if (!Array.isArray(flags) || !flags.length) return null;
  return (
    <div className="invoice-correction-panel" role="status">
      <div className="invoice-correction-head">
        <span className="material-symbols-rounded">edit_document</span>
        <strong>Needs correcting</strong>
      </div>
      {flags.map(f => (
        <div key={f.id} className="invoice-correction-flag">
          <p>{f.note}</p>
          <p className="invoice-correction-meta">
            {f.money_difference != null && Math.abs(f.money_difference) >= 0.005
              ? `${money(f.money_difference)} ${f.money_difference < 0 ? 'over-charged' : 'under-charged'} · ` : ''}
            Flagged{f.flagged_by ? ` by ${f.flagged_by}` : ''}{f.flagged_at ? ` on ${new Date(f.flagged_at).toLocaleDateString('en-GB')}` : ''}
            {f.order_number ? ` · ${f.order_number}` : ''}
          </p>
          <div>
            <button type="button" className="btn-slideout" disabled={!canResolve}
              title={canResolve ? 'Mark it dealt with — say what put it right' : 'Marking an invoice corrected needs "Create invoices" or "Edit invoices"'}
              onClick={() => setResolving(f)}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">task_alt</span></div>
              <span className="btn-label">Dealt with…</span>
            </button>
          </div>
        </div>
      ))}
      {resolving && (
        <RepFlagResolveModal
          flag={{ id: resolving.id }}
          title="Invoice correction — dealt with"
          customerName={customerName}
          documentsUrl={`${API_URL}/api/invoices/correction-flags/${resolving.id}/documents`}
          heading={(
            <div className="rp-resolve-flag">
              <span className="material-symbols-rounded rp-flag-icon">edit_document</span>
              <div><div className="rp-flag-note">{resolving.note}</div></div>
            </div>
          )}
          submit={async (document, note) => {
            const res = await fetch(`${API_URL}/api/invoices/correction-flags/${resolving.id}/resolve`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
              body: JSON.stringify({ note, ...(document ? { document } : {}) })
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok || !body.success) throw new Error(body.error || 'Not marked dealt with');
            return body;
          }}
          onClose={() => setResolving(null)}
          onDone={() => { setResolving(null); if (onResolved) onResolved(); }}
        />
      )}
    </div>
  );
}

export default InvoiceCorrectionPanel;
